import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { weeklyProgramsAPI } from '../services/api'
import {
  CalendarIcon,
  PlusIcon,
  ArrowPathIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { getWeekNumber } from '../components/workouts/workoutHelpers'
import { ProgramCard } from '../components/workouts/ProgramCard'
import { ProgramFormModal, CopyProgramModal } from '../components/workouts/ProgramFormModal'
import { ReportModal } from '../components/workouts/ReportModal'
import { ProgramDetailModal } from '../components/workouts/ProgramDetailModal'
import { AIPlanGeneratorDialog } from '../components/workout-planner/AIPlanGeneratorDialog'

export default function WeeklyPrograms() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()
  const currentWeek = getWeekNumber(new Date())

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingProgram, setEditingProgram] = useState(null)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copySourceProgram, setCopySourceProgram] = useState(null)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportProgram, setReportProgram] = useState(null)
  const [expandedProgram, setExpandedProgram] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [detailProgram, setDetailProgram] = useState(null)
  const [showAIPlan, setShowAIPlan] = useState(false)

  // Fetch weekly programs
  const { data: programsData, isLoading } = useQuery({
    queryKey: ['weeklyPrograms', selectedYear, statusFilter],
    queryFn: () => weeklyProgramsAPI.getPrograms({
      year: selectedYear,
      status: statusFilter || undefined,
      limit: 53
    })
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ id, force }) => weeklyProgramsAPI.deleteProgram(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
    }
  })

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: weeklyProgramsAPI.submitProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
    }
  })

  const handleDelete = (id, status) => {
    if (status === 'submitted') {
      return
    }
    if (confirm(t('programs.confirmDelete'))) {
      deleteMutation.mutate({ id, force: false })
    }
  }

  const handleEdit = (program) => {
    setEditingProgram(program)
    setShowModal(true)
  }

  const handleAdd = () => {
    setEditingProgram(null)
    setShowModal(true)
  }

  const handleCopy = (program) => {
    setCopySourceProgram(program)
    setShowCopyModal(true)
  }

  const handleSubmit = (id) => {
    if (confirm(t('programs.submitConfirm'))) {
      submitMutation.mutate(id)
    }
  }

  const handleViewReport = (program) => {
    setReportProgram(program)
    setShowReportModal(true)
  }

  const handleViewDetail = (program) => {
    setDetailProgram(program)
    setShowDetailModal(true)
  }

  // Memoize programs to prevent useMemo dependency from changing every render
  const programs = useMemo(() => programsData?.data?.programs || [], [programsData])

  // Generate year options (current year and previous 2 years)
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  // Filter by date range and sort programs by week number descending
  const sortedPrograms = useMemo(() => {
    let filtered = [...programs]
    if (dateFrom) {
      filtered = filtered.filter(p => p.end_date >= dateFrom)
    }
    if (dateTo) {
      filtered = filtered.filter(p => p.start_date <= dateTo)
    }
    return filtered.sort((a, b) => b.week_number - a.week_number)
  }, [programs, dateFrom, dateTo])

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('programs.title')}</h1>
            <p className="mt-2 text-gray-600">
              {t('programs.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowAIPlan(true)}
              className="btn-outline flex items-center"
            >
              <SparklesIcon className="h-5 w-5 mr-2" />
              {t('aiPlan.button')}
            </button>
            <button
              onClick={handleAdd}
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('programs.createProgram')}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Year Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('programs.year')}
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="w-full focus:border-primary-500 focus:ring-primary-500"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('common.status')}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">{t('common.all')}</option>
            <option value="draft">{t('programs.status.draft')}</option>
            <option value="active">{t('programs.status.active')}</option>
            <option value="submitted">{t('programs.status.submitted')}</option>
          </select>
        </div>

        {/* Date From Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('dateFilter.from')}
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        {/* Date To Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('dateFilter.to')}
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        {/* Current Week Shortcut */}
        <div className="flex items-end gap-2">
          <button
            onClick={() => {
              setSelectedYear(currentYear)
              setStatusFilter('')
              setDateFrom('')
              setDateTo('')
            }}
            className="btn-outline flex items-center"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {t('programs.week')} {currentWeek}
          </button>
        </div>
      </div>

      {/* Programs List */}
      {sortedPrograms.length > 0 ? (
        <div className="space-y-4">
          {sortedPrograms.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              isExpanded={expandedProgram === program.id}
              onToggleExpand={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
              onEdit={() => handleEdit(program)}
              onDelete={() => handleDelete(program.id, program.status)}
              onCopy={() => handleCopy(program)}
              onSubmit={() => handleSubmit(program.id)}
              onViewReport={() => handleViewReport(program)}
              onViewDetail={() => handleViewDetail(program)}
              t={t}
              i18n={i18n}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('programs.noPrograms')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {statusFilter ? t('programs.adjustFilters') : t('programs.noProgramsHint')}
          </p>
          {!statusFilter && (
            <div className="mt-6">
              <button onClick={handleAdd} className="btn-primary flex items-center mx-auto">
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('programs.createProgram')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Program Modal */}
      {showModal && (
        <ProgramFormModal
          program={editingProgram}
          onClose={() => {
            setShowModal(false)
            setEditingProgram(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
            setShowModal(false)
            setEditingProgram(null)
          }}
        />
      )}

      {/* Copy Program Modal */}
      {showCopyModal && copySourceProgram && (
        <CopyProgramModal
          sourceProgram={copySourceProgram}
          onClose={() => {
            setShowCopyModal(false)
            setCopySourceProgram(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })
            setShowCopyModal(false)
            setCopySourceProgram(null)
          }}
        />
      )}

      {/* VH-226 AI plan generator */}
      {showAIPlan && (
        <AIPlanGeneratorDialog
          onClose={() => setShowAIPlan(false)}
          onAccepted={() => queryClient.invalidateQueries({ queryKey: ['weeklyPrograms'] })}
        />
      )}

      {/* Report Modal */}
      {showReportModal && reportProgram && (
        <ReportModal
          program={reportProgram}
          onClose={() => {
            setShowReportModal(false)
            setReportProgram(null)
          }}
        />
      )}

      {/* Detail Modal - View and Log Workouts */}
      {showDetailModal && detailProgram && (
        <ProgramDetailModal
          program={detailProgram}
          onClose={() => {
            setShowDetailModal(false)
            setDetailProgram(null)
          }}
        />
      )}
    </div>
  )
}
